import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import HeaderMain from "../../components/common/HeaderMain/HeaderMain";
import LikeCounter from "../../components/common/LikeCounter/LikeCounter";
import SharePageButton from "../../components/common/SharePageButton/SharePageButton";
import Tabs from "../../components/common/Tabs/Tabs";
import NotableMoments from "../../components/single/NotableMoments/NotableMoments";
import Footer from "../../components/single/Footer/Footer";
import FallbackImage from "../../components/common/FallbackImage/FallbackImage";
import styles from "./NFTBuy.module.css";
import { apiFetch, API_BASE } from "../../utils/api";
import * as Sentry from "@sentry/react";

const tabs = ["Details", "Activity"];

const NFTBuy = () => {
  const { descriptor } = useParams();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState("Details");

  const [memory, setMemory] = useState(null);
  const [creator, setCreator] = useState(null);
  const [likes, setLikes] = useState(0);
  const [views, setViews] = useState(0);
  const [sales, setSales] = useState([]);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    const fetchMemoryDetails = async () => {
      try {
        const res = await apiFetch(`/api/memory/${descriptor}/details`,{}, false, true);
        setMemory(res.memory);

        setCreator(res.creator)
        setLikes(res.likes);
        setViews(res.views);
        setSales(res.sales || []);
        setLiked(res.likedByUser);


        const memoryData = res.memory;
        console.log(memoryData);
        // Set page title and meta tags dynamically
        if (memoryData?.title || memoryData?.description || memoryData?.file_path) {
          document.title = `${memoryData.title}`;

          const setMeta = (name, content, attr = "name") => {
            let tag = document.querySelector(`meta[${attr}="${name}"]`);
            if (!tag) {
              tag = document.createElement("meta");
              tag.setAttribute(attr, name);
              document.head.appendChild(tag);
            }
            tag.setAttribute("content", content || "");
          };

          const imageUrl = memoryData.file_path?.startsWith("http")
            ? memoryData.file_path
            : `${window.location.origin}${memoryData.file_path}`;

          setMeta("description", memoryData.description || "Own a unique memory forever.");
          setMeta("og:title", memoryData.title, "property");
          setMeta("og:description", memoryData.description, "property");
          setMeta("og:image", imageUrl, "property");
          setMeta("og:url", window.location.href, "property");
          setMeta("og:type", "website", "property");

          setMeta("twitter:card", "summary_large_image");
          setMeta("twitter:title", memoryData.title);
          setMeta("twitter:description", memoryData.description);
          setMeta("twitter:image", imageUrl);
        }
      } catch (err) {
        Sentry.captureException(err);
        console.error("Failed to load memory details", err);
      }
    };

    fetchMemoryDetails();
  }, [descriptor]);

  const handleBuyClick = useCallback(() => {
    navigate(`/buy/${descriptor}`);
  }, [navigate, descriptor]);

  const handleTabChange = (tab) => setSelectedTab(tab);

  const truncateName = (name) => (name.length > 128 ? name.substring(0, 10) + "..." : name);

  if (!memory) return null;

  return (
    <Box className={styles.nftBuy}>
      <HeaderMain />

      <Box className={styles.innerelement}>
        <Box className={styles.artParent}>
          {memory.amount !== 0 && memory.sold >= memory.amount && (
            <Box className={styles.soldOutBanner}>Sold Out</Box>
          )}
          <FallbackImage className={styles.artIcon} alt="" src={`${API_BASE}${memory.file_path}`} type={'image'} />
          {creator?.descriptor && (
            <Box className={styles.creatorBox}>
              <Typography className={styles.createdByText}>
                Created by{" "}
                <span
                  className={styles.creatorLink}
                  onClick={() => navigate(`/creator/${creator.descriptor}`)}
                >
                  {`${creator.name} ${creator.surname}` || creator.descriptor}
                </span>
              </Typography>
            </Box>
          )}
          <Box className={styles.auction}>
            <Box className={styles.title}>
              <Typography className={styles.titleText}>{memory.title}</Typography>
              <Typography className={styles.name}>{memory.description}</Typography>
            </Box>
            <Box className={styles.topBid}>
              <Typography className={styles.subtitle}>Price</Typography>
              <Typography className={styles.eth}>${memory.price}</Typography>
            </Box>
            {memory.amount !== 0 && memory.sold >= memory.amount ? (
              <Typography className={styles.soldOutText}>Sold Out</Typography>
            ) : (
              <Button className={styles.buyButton} onClick={handleBuyClick}>Buy</Button>
            )}
          </Box>
        </Box>

        <Box className={styles.sidebar}>
          <Box className={styles.stats}>
            <LikeCounter
              id={memory.uuid}
              initialLikes={likes}
              isLiked={liked}
              whiteBackground={true}
            />
            <SharePageButton whiteBackground={true} title={memory.title} />
          </Box>
          <Box className={styles.activity}>
            <Tabs tabs={tabs} onTabChange={handleTabChange} />
            {selectedTab === "Details" && memory && (
              <Box className={styles.detailsSection}>
                <Typography variant="h6" className={styles.detailsTitle}>Description</Typography>
                <Typography className={styles.detailsContent}>{memory.description}</Typography>

                <Typography variant="h6" className={styles.detailsTitle} sx={{ marginTop: "1rem" }}>Availability</Typography>
                <Typography className={styles.detailsContent}>
                  {memory.amount === 0 ? "Unlimited" : (
                    <>
                      Total: {memory.amount}<br />
                      Sold: {memory.sold}<br />
                      Available: {memory.amount - memory.sold}
                    </>
                  )}
                </Typography>

                {memory.tags?.length > 0 && (
                  <>
                    <Typography variant="h6" className={styles.detailsTitle} sx={{ marginTop: "1rem" }}>Tags</Typography>
                    <Typography className={styles.detailsContent}>
                      {memory.tags.join(", ")}
                    </Typography>
                  </>
                )}
              </Box>
            )}
            {selectedTab === "Activity" && memory && (
              <Box className={styles.list}>
                {sales.map((item, index) => (
                  <Box key={index} className={styles.activityItemV2Onlight}>
                    <Box className={styles.topSection}>
                      <Box className={styles.bid}>Purchased:</Box>
                      <Box className={styles.price}>
                        <Typography className={styles.bidAmount}><b>{item.price} {item.currency}</b></Typography>
                      </Box>
                    </Box>
                    <Box className={styles.bottomSection}>
                      <Box className={styles.details}>
                        <Box className={styles.bid}>by</Box>
                        <Typography className={styles.link}><b>{item.buyer_name.trim() ? truncateName(item.buyer_name) : "Anonymous"}</b></Typography>
                        <Box className={styles.bid}>{new Date(item.sale_date).toLocaleString()}</Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <NotableMoments 
        creator={descriptor} 
        heading={
          creator?.name && creator?.surname 
            ? `Other memories from ${creator.name} ${creator.surname}` 
            : "Other memories"
        } 
      />
      <Footer />
    </Box>
  );
};

export default NFTBuy;